// @flow

import React from "react";
import AdressContainer from "../../container/checkout/mainContentBox/AdressContainer";
import OrderSummaryContainer from "../../container/checkout/mainContentBox/OrderSummaryContainer";
import "./CheckoutPage.scss";
import OrderFinishedContainer from "../../container/checkout/mainContentBox/OrderFinishedContainer";
import type {translate} from "../../i18next";
import Loading from "../layout/Loading";
import {ChevronLeft} from "lucide-react";
import Icon from "../icons/Icon";

export type CheckoutPageProps = {
	t: translate,
    isLoading: boolean,
	isCartEmpty: boolean,
	showOrderFinishPage: boolean,
	setPage: (page: number) => void,
	page: number,
	maxPage: number,
	minPage: number,
	isPaymentPending: boolean,
	hasPaymentMethods: boolean,
	validateCart: () => void,
    loadPaymentMethods: () => void,
}

// The cart is no step of its own: it is the drawer over the shop (CartDrawer.js), whose
// "to the checkout" leads to the address. Payment and overview are one page, "review and pay".
export const CHECKOUT_PAGE_STATUS = {
	ADRESS: 0,
	OVERVIEW: 1,
	FINISH: 2
};

const CHECKOUT_STEPS = [
	{page: CHECKOUT_PAGE_STATUS.ADRESS, label: "cart.checkoutSteps.step2"},
	{page: CHECKOUT_PAGE_STATUS.OVERVIEW, label: "cart.checkoutSteps.review"},
	{page: CHECKOUT_PAGE_STATUS.FINISH, label: "cart.checkoutSteps.step5"},
];

class CheckoutPage extends React.Component<CheckoutPageProps> {
	constructor(props) {
		super(props);
		this.nextPage = this.nextPage.bind(this);
		this.prevPage = this.prevPage.bind(this);
		this.setPage = this.setPage.bind(this);
		this.renderPage = this.renderPage.bind(this);
	}

	componentDidMount() {
		// Loaded by the cart drawer already, as a rule. Loading them anew would drop the payment
		// chosen there - PayPal Express, which leads here paid.
		if (!this.props.hasPaymentMethods) {
			this.props.loadPaymentMethods();
		}
		if (!this.props.isCartEmpty) {
			this.props.validateCart();
		}
	}

	nextPage() {
		this.setPage(this.props.page + 1);
	}

	prevPage() {
		if (this.props.page) {
			this.setPage(this.props.page - 1);
		} else {
			// #55678: before the address there is no checkout page - back to where the cart was
			// opened, which opens it again (CartDrawer.js)
			window.history.back();
		}
	}

	setPage(page: number) {
		this.props.setPage(page);
	}

	renderPage() {
		if (this.props.isLoading) {
			return <Loading/>;
		}

		switch (this.props.page) {
			case CHECKOUT_PAGE_STATUS.ADRESS:
				return <AdressContainer nextPage={this.nextPage}/>;
			case CHECKOUT_PAGE_STATUS.OVERVIEW:
				return <OrderSummaryContainer/>;
			case CHECKOUT_PAGE_STATUS.FINISH:
				return <OrderFinishedContainer/>;
			default:
				return <AdressContainer nextPage={this.nextPage}/>;
		}
	}

	renderSlider() {
		const handleClick = page => {
			if (page <= this.props.maxPage && page >= this.props.minPage) {
				return this.setPage(page);
			}
		};

		return (
			<div>
				<div className="hpm-CheckoutNavigation">
					<button type={"button"} onClick={() => this.prevPage()}>
						<Icon icon={ChevronLeft} size={28}/>
						{this.props.t("cart.back")}
					</button>
					<div className="hpm-Step">
						{this.props.t("cart.mobileNav", {
							current: this.props.page + 1,
							total: CHECKOUT_PAGE_STATUS.FINISH + 1
						})}
					</div>
				</div>
				<div className="hpm-CheckoutProgressBar" style={{"--hpm-checkoutSteps": CHECKOUT_STEPS.length}}>
					<div className="hpm-bar">
						<div
							style={{transform: `translate(${-100 + 100 * this.props.page / CHECKOUT_PAGE_STATUS.FINISH}%)`}}
							className="hpm-value"
						/>
					</div>
					{CHECKOUT_STEPS.map(step =>
						<button
							key={step.page}
							type="button"
							className={this.props.page >= step.page ? "hpm-active" : ""}
							onClick={() => handleClick(step.page)}
						>
							{this.props.t(step.label)}
						</button>
					)}
				</div>
			</div>
		);
	}

	render() {

		return this.props.showOrderFinishPage
			? <OrderFinishedContainer/>
			: this.props.isCartEmpty && !this.props.isPaymentPending
				? <div className="hpm-CheckoutSlider hpm-emptyCart">
					<div>
						<span className={"hpm-emptyCartText"}>{this.props.t("cart.empty")}</span>
						<button
							type={"button"}
							onClick={() => window.history.back()}
							className="hpm-emptyCheckoutButton back">{this.props.t("cart.back")}
                        </button>
					</div>
				</div>
				: <div className="hpm-CheckoutSlider">
					<div
						className="hpm-cartClose"
						onClick={() => {
							window.history.back();
						}}
						onKeyDown={e => {
							if (e.key === "Enter" || e.key === " ") {
								window.history.back();
							}
						}}
						role="button"
						tabIndex={0}
						aria-label={this.props.t("cart.back")}
					/>
					{this.renderSlider()}
					{this.renderPage()}
				</div>;
	}
}

export default CheckoutPage;
